<template>
	<div class="menu-wrapper">
		<iframe v-if="menuData.showType === 1" :src="menuData.menuUrl" frameborder="0" class="menu-iframe"></iframe>
		<CScrollPro v-else ref="scrollRef" pullDown @pull-down="scrollPullDown">
			<div ref="contentBlockRef" class="menu-module-block">
				<!-- 企业品牌信息 -->
				<SmartLayoutC v-show="props.isIndex" compId="brand_header" mode="basic" />
				<!-- 标准模块展示 -->
				<div
					v-for="item in getModules"
					:key="item.moduleId"
					:data-module-id="item.moduleId"
					class="menu-module"
					:class="{
						'menu-module--white': whiteStyleModule(item),
						'menu-module--gradient': gradientStyleModule(item)
					}"
				>
					<MenuModuleHeader v-if="!hideTitle(item)" :item="item" />
					<SmartDisplayC
						:key="getModuleKey(item)"
						:compId="getModuleId(item)"
						mode="overview"
						v-bind="getModuleProps(item)"
					>
						<template #footer="{ moduleData }">
							<MenuModuleFooter @click="handleModuleClick(item, moduleData)" />
						</template>
					</SmartDisplayC>
				</div>
			</div>
		</CScrollPro>
	</div>
</template>

<script setup>
import { ref, watch, onActivated, nextTick, onMounted } from 'vue';
import CScrollPro from '@rebuild/components/CScrollPro.vue';
import SmartLayoutC from '@rebuild/components/SmartLayoutC.vue';
import SmartDisplayC from '@rebuild/components/SmartDisplayC.vue';
import { useModuleAttrs } from '@rebuild/hooks/useModuleAttrs';
import MenuModuleHeader from '@rebuild/materials/shared/menu_container/MenuModuleHeader.vue';
import MenuModuleFooter from '@rebuild/materials/shared/menu_container/MenuModuleFooter.vue';
import { useBusEvent } from '@rebuild/hooks/useBusEvent';
import { useHeightObserver } from '@rebuild/hooks/useHeightObserver';
import { useMenuModuleClick } from '@rebuild/materials/shared/menu_container/useMenuModuleClick';

const props = defineProps({
	menuData: {
		type: Object,
		default: () => ({})
	},
	isIndex: {
		type: Boolean,
		default: false
	}
});

const scrollRef = ref(null);
const scrollPullDown = async done => {
	await nextTick();
	refreshModules();
	console.log('下拉刷新完成');
	done();
};
const refreshScroll = () => {
	scrollRef.value?.refresh();
};
onActivated(() => {
	// 避免下拉刷新过程中切换了组件激活状态导致滚动错误 所以每次激活要刷新一次
	refreshScroll();
});

const whiteStyleModule = item => {
	const includesName = ['banner', 'quote', 'quote_hk02276'];
	return !includesName.includes(item.moduleId);
};
const gradientStyleModule = item => {
	const includesName = ['quarterly_performance'];
	return includesName.includes(item.moduleId);
};
const hideTitle = item => {
	const includesName = ['banner', 'quote', 'quote_hk02276'];
	return item.type === 2 || includesName.includes(item.moduleId);
};

const { getModuleId, getModuleProps, getModuleKey, createModuleKey } = useModuleAttrs();
const { handleModuleClick } = useMenuModuleClick();

// 高度变化观察器 - 自动在 onMounted 时开始观察 contentBlockRef
const contentBlockRef = ref(null);
useHeightObserver({
	targetRef: contentBlockRef,
	onHeightChange: () => {
		// 高度变化时刷新滚动容器
		nextTick(() => {
			refreshScroll();
		});
	}
});

const getModules = ref([]);
const refreshModules = async () => {
	const menuData = props.menuData;
	const source = (menuData && menuData.moduleVOS) || [];
	getModules.value = source.map(item => ({
		...item,
		_key: createModuleKey()
	}));
};

onMounted(() => {
	const menuData = props.menuData;
	getModules.value = (menuData && menuData.moduleVOS) || [];
});

const updateMenuContext = async () => {
	await nextTick();
	refreshModules();
};

const scrollToModule = async moduleId => {
	await nextTick();
	if (moduleId && scrollRef.value) {
		// 查找对应的模块元素
		const moduleElement = document.querySelector(`[data-module-id="${moduleId}"]`);
		if (moduleElement) {
			scrollRef.value.scrollToElement(moduleElement, 200);
		}
	}
};
useBusEvent('iframe-message', async msgData => {
	const { activeModuleId } = msgData;
	if (activeModuleId) {
		scrollToModule(activeModuleId);
	}
});
watch(
	() => props.menuData,
	() => {
		updateMenuContext();
	},
	{ deep: true }
);
</script>

<style lang="less" scoped>
.menu-wrapper {
	--menu-slide-padding: 0.14rem;
	height: 100%;

	.menu-iframe {
		width: 100%;
		height: 100%;
	}
}
.menu-module-block {
	// create BFC
	&::before,
	&::after {
		content: '';
		display: table;
		height: 0;
	}
}
.menu-module {
	margin: var(--menu-slide-padding);
	position: relative;

	&.menu-module--white {
		padding: 0 0.12rem;
		border-radius: 0.04rem;
		background: #fff;
		overflow: hidden;
	}
	&.menu-module--gradient {
		background: linear-gradient(180deg, #e3ebff 0%, #fff 64.38%), #fff;
	}
}
</style>
